import { FieldArray, Formik } from 'formik';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { getEnumsFromState, getSelectedCountry } from '../../../../../states/slices/resourceSlice';
import {
  createIntermediary,
  deleteIntermediaryMotorPolicy,
  deleteInsurerContact,
  getInsurerContacts,
  getIntermediaryMotorPolicies,
  saveInsurerContacts,
  saveIntermediaryMotorPolicies,
  updateIntermediary
} from '../../../../../api/requests';
import Input from '../../../../../components/form-control/Input';
import Button from '../../../../../components/form-control/Button';
import { INTERMEDIARY_TYPES } from '../../../../../resources/enums';
import SelectDropdown, {
  ISelectDropdownItem
} from '../../../../../components/form-control/SelectDropdown';
import useUserAccount from '../../../../../hooks/userAccountHook';
import Loader from '../../../../../components/Loader';
import { BrokerInsurerIntegration } from '..';
import { useQuery } from '@tanstack/react-query';
import { HelpCircle, Plus, Trash2 } from 'lucide-react';
import Tooltip from '../../../../../components/ToolTip';
import { getAllInsurers } from '../../../../../services/insurer';
import { capitalizeSentence, sentenceCase, showToast } from '../../../../../utils/miscFunctions';
import { POLICY_ISSUANCE_MODES_WITH_DESCRIPTION } from '../../../../../resources/constants';

export interface IMotorPolicyFormValue {
  id?: number;
  name: string;
  rate: number | string;
}

export interface IInsurerContactFormValue {
  id?: number;
  name: string;
  phone: string;
  email: string;
  role: string;
}

const DEFAULT_MOTOR_POLICIES: IMotorPolicyFormValue[] = [
  { name: 'Comprehensive', rate: '' },
  { name: 'Third Party', rate: '' }
];

const EMPTY_CONTACT: IInsurerContactFormValue = { name: '', phone: '', email: '', role: '' };

const findRateByName = (policies: IMotorPolicyFormValue[], needle: string): number | undefined => {
  const match = policies.find((p) => p.name?.trim().toLowerCase() === needle.toLowerCase());
  const rate = match?.rate;
  return rate === '' || rate === undefined || rate === null ? undefined : Number(rate);
};

interface ICreateIntermediaryProps {
  onClose: () => void;
  onSuccess: () => void;
  intermediary: BrokerInsurerIntegration | undefined;
  isCreateRequest?: boolean;
  readOnly?: boolean;
}

export interface IIntermediaryDTO {
  apikey: string;
  branchCode: string;
  brokerId: number;
  brokerName: string;
  insurerId: number;
  insurerName: string;
  intermediaryCode: string;
  comprehensiveCommissionRate: number;
  thirdPartyCommissionRate: number;
  type: INTERMEDIARY_TYPES;
  insurerContactEmails?: string;
  issuanceMode: string;
  midReady: boolean;
}

const CreateBrokerIntermediary = ({
  onClose,
  onSuccess,
  intermediary,
  isCreateRequest,
  readOnly
}: ICreateIntermediaryProps): JSX.Element => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [deletedPolicyIds, setDeletedPolicyIds] = useState<number[]>([]);
  const [deletedContactIds, setDeletedContactIds] = useState<number[]>([]);
  const { user } = useUserAccount();
  const country = useSelector(getSelectedCountry);
  const enums = useSelector(getEnumsFromState)
  const issuanceModes: any[] = enums?.issuanceModes || [];

  const persistMotorPolicies = async (intermediaryId: number, policies: IMotorPolicyFormValue[]) => {
    await Promise.all(deletedPolicyIds.map((policyId) => deleteIntermediaryMotorPolicy(policyId).catch(() => null)));
    const payload = policies
      .filter((p) => p.name?.trim())
      .map((p) => ({
        id: p.id,
        name: p.name.trim(),
        rate: p.rate === '' || p.rate === null || p.rate === undefined ? null : Number(p.rate)
      }));
    await saveIntermediaryMotorPolicies(intermediaryId, payload);
  };

  const persistInsurerContacts = async (insurerId: number, contacts: IInsurerContactFormValue[]) => {
    await Promise.all(deletedContactIds.map((contactId) => deleteInsurerContact(contactId).catch(() => null)));
    const payload = contacts
      .filter((c) => c.name?.trim() || c.email?.trim() || c.phone?.trim())
      .map((c) => ({
        id: c.id,
        name: c.name.trim(),
        phone: c.phone.trim(),
        email: c.email.trim(),
        role: c.role.trim()
      }));
    await saveInsurerContacts(insurerId, payload);
  };

  const updateSelectedIntermediary = async (
    payload: IIntermediaryDTO,
    policies: IMotorPolicyFormValue[],
    contacts: IInsurerContactFormValue[]
  ) => {
    if (intermediary) {
      setSubmitting(true)
      try {
        const response: any = await updateIntermediary(intermediary?.id, payload);
        await persistMotorPolicies(intermediary.id, policies);
        if (payload.insurerId) {
          await persistInsurerContacts(payload.insurerId, contacts);
        }
        showToast.success(response.message || 'Intermediary updated successfully');
        onSuccess();
        onClose();
      } catch (error: any) {
        showToast.error(error.description ?? 'Failed to update intermediary');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const createNewIntermediary = async (
    payload: IIntermediaryDTO,
    policies: IMotorPolicyFormValue[],
    contacts: IInsurerContactFormValue[]
  ) => {
    try {
      // Persist contacts before creating the intermediary so the backend's
      // partnership notification email can resolve the primary contact.
      if (payload.insurerId) {
        await persistInsurerContacts(payload.insurerId, contacts);
      }
      const response: any = await createIntermediary(payload);
      const newId = response?.data?.id ?? response?.id;
      if (newId) {
        await persistMotorPolicies(newId, policies);
      }
      showToast.success(response.message || 'Intermediary created successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      showToast.error(error.description ?? 'Failed to create intermediary');
    } finally {
      setSubmitting(false);
    }
  };

  const getAllBrollyInsurers = async () => {
    if (!country?.id) return;
    try {
      const response: any = await getAllInsurers(country?.id);
      if (response) {
       
        const allInsurers = response?.data?.map((insurer: any) => ({
          name: `${insurer.name}`,
          value: Number(insurer.id)
        }));
        return allInsurers;
      }
    } catch (error) {
      console.log(error);
    }
  };


  const { data: insurers, isLoading: insurersLoading } = useQuery({
    queryKey: ['getAllBrollyInsurers'],
    queryFn: getAllBrollyInsurers,
  });

  const { data: existingMotorPolicies, isLoading: motorPoliciesLoading } = useQuery<IMotorPolicyFormValue[]>({
    queryKey: ['getIntermediaryMotorPolicies', intermediary?.id],
    queryFn: async () => {
      if (!intermediary?.id) return [];
      const response: any = await getIntermediaryMotorPolicies(intermediary.id);
      const raw = Array.isArray(response) ? response : response?.data ?? [];
      return raw.map((p: any) => ({ id: p.id, name: p.name, rate: p.rate ?? '' }));
    },
    enabled: !!intermediary?.id,
  });

  const { data: existingContacts, isLoading: contactsLoading } = useQuery<IInsurerContactFormValue[]>({
    queryKey: ['getInsurerContacts', intermediary?.insurerId],
    queryFn: async () => {
      if (!intermediary?.insurerId) return [];
      const response: any = await getInsurerContacts(intermediary.insurerId);
      const raw = Array.isArray(response) ? response : response?.data ?? [];
      return raw.map((c: any) => ({
        id: c.id,
        name: c.name ?? '',
        phone: c.phone ?? '',
        email: c.email ?? '',
        role: c.role ?? ''
      }));
    },
    enabled: !!intermediary?.insurerId,
  });

  const initialMotorPolicies: IMotorPolicyFormValue[] = (() => {
    if (existingMotorPolicies && existingMotorPolicies.length > 0) return existingMotorPolicies;
    if (intermediary) {
      return [
        { name: 'Comprehensive', rate: intermediary.comprehensiveCommissionRate ?? '' },
        { name: 'Third Party', rate: intermediary.thirdPartyCommissionRate ?? '' }
      ];
    }
    return DEFAULT_MOTOR_POLICIES;
  })();

  const initialContacts: IInsurerContactFormValue[] = (() => {
    if (existingContacts && existingContacts.length > 0) return existingContacts;
    if (intermediary?.insurerContactEmails) {
      const emails = intermediary.insurerContactEmails
        .split(',')
        .map((e: string) => e.trim())
        .filter((e: string) => e.length > 0);
      if (emails.length > 0) {
        return emails.map((email: string) => ({ ...EMPTY_CONTACT, email }));
      }
    }
    return [{ ...EMPTY_CONTACT }];
  })();

  return (
    <>
      {insurersLoading || motorPoliciesLoading || contactsLoading ? (

          <Loader message="Fetching insurers. Please wait..." brollyLoaderFixed />

      ) : (
        <div className="p-6">
          <Formik
            initialValues={{
              apikey: intermediary?.apikey || '',
              branchCode: intermediary?.branchCode || '',
              intermediaryCode: intermediary?.intermediaryCode || '',
              brokerId: intermediary?.brokerId,
              brokerName: intermediary?.brokerName || '',
              insurerId: intermediary?.insurerId || null,
              insurerName: intermediary?.insurerName || '',
              token: intermediary?.token || '',
              motorPolicies: initialMotorPolicies,
              contacts: initialContacts,
              type: intermediary?.type || '',
              issuanceMode: intermediary?.issuanceMode.toLowerCase() || '',
            }}
            enableReinitialize={true}
            validateOnChange={true}
            validateOnBlur={true}
            validationSchema={Yup.object().shape({
              insurerId: Yup.string().required('Insurer is required.').nullable(),
              issuanceMode: Yup.string().trim().required('Issuance mode is required.'),
              type: !user.isBroker() ? Yup.string().trim().required('Type is required.') : Yup.string().trim(),
              motorPolicies: Yup.array().of(
                Yup.object().shape({
                  name: Yup.string().trim().required('Policy name is required.'),
                  rate: Yup.number().typeError('Rate must be a number.').min(0, 'Rate must be positive.').nullable()
                })
              ).min(1, 'At least one motor policy is required.'),
              contacts: Yup.array().of(
                Yup.object().shape({
                  name: Yup.string().trim().required('Contact name is required.'),
                  email: Yup.string().trim().email('Enter a valid email.').required('Email is required.'),
                  phone: Yup.string().trim(),
                  role: Yup.string().trim()
                })
              ).min(1, 'At least one contact is required.')
            })}
            onSubmit={async (values: any, { resetForm }) => {
              const policies: IMotorPolicyFormValue[] = values.motorPolicies ?? [];
              const contacts: IInsurerContactFormValue[] = values.contacts ?? [];
              const joinedEmails = contacts
                .map((c) => c.email?.trim())
                .filter((e): e is string => !!e)
                .join(',');
              const payload: IIntermediaryDTO = {
                apikey: values.apikey,
                branchCode: values.branchCode,
                brokerId: values.brokerId,
                insurerContactEmails: joinedEmails,
                brokerName: values.brokerName,
                insurerId: values.insurerId,
                insurerName: values.insurerName,
                intermediaryCode: values.intermediaryCode,
                comprehensiveCommissionRate: findRateByName(policies, 'Comprehensive') ?? 0,
                thirdPartyCommissionRate: findRateByName(policies, 'Third Party') ?? 0,
                type: !user.isBroker() ? values.type : INTERMEDIARY_TYPES.BROKER,
                issuanceMode: values.issuanceMode.toUpperCase(),
                midReady: values.issuanceMode.toUpperCase() === 'MID_LIVE' ? true : false,
              };

              isCreateRequest
                ? createNewIntermediary(payload, policies, contacts)
                : updateSelectedIntermediary(payload, policies, contacts);
            }}>
            {({
              values,
              errors,
              isSubmitting,
              handleSubmit,
              handleChange,
              handleBlur,
              setFieldValue,
              touched
            }) => {
              console.log("values",values)
              return (
                <form className="" onSubmit={handleSubmit}>
                  <fieldset disabled={readOnly} className="contents">
                  <div className="w-full flex flex-col space-y-8">
                    <SelectDropdown
                      name="insurerId"
                      listSelectedValue={
                        values.insurerId ? values.insurerId : intermediary?.insurerId
                      }
                      list={insurers?.map((insurer: ISelectDropdownItem) => ({
                        name: insurer.name,
                        value: insurer.value
                      }))}
                      onChange={(value: number) => {
                        const insurer = insurers?.find(
                          (insurer: ISelectDropdownItem) => insurer.value === value
                        );
                        setFieldValue('insurerId', insurer?.value);
                        setFieldValue('insurerName', insurer?.name);
                      }}
                      onBlur={handleBlur}
                      label="Insurer Company"
                      placeholder="Select an insurer company"
                      required
                      error={touched.insurerId ? errors.insurerId : undefined}
                      helperText={touched?.insurerId ? errors.insurerId : undefined}
                    />

                    <SelectDropdown
                      name="issuanceMode"
                      listSelectedValue={values.issuanceMode}
                      list={issuanceModes?.map((mode:any) => ({
                        name: mode.title,
                        value: mode.key,
                        description: mode.description
                      }))}
                      showFullDescription
                      onChange={(value: string) => {
                         setFieldValue('issuanceMode', value);
                      }}
                      onBlur={handleBlur}
                      label="Issuance Mode"
                      placeholder="Select an issuance mode"
                      classes='!max-h-[350px]'
                      required
                      error={touched.issuanceMode ? errors.issuanceMode : undefined}
                      helperText={touched?.issuanceMode ? errors.issuanceMode : undefined}
                      appendLabelIcon={
                        <Tooltip
                          placement="bottom"
                          bgColor="bg-black"
                          textColor="text-white"
                          containerClassName="rounded-full"
                          tooltipClassName="w-[350px] text-xs"
                          message={"Select the preferred policy issuance mode between you and the chosen insurance company. This determines how policies are transmitted and processed—via MID, direct insurer API integration, or email."}>
                          <HelpCircle className='h-3 w-3 ml-1 cursor-pointer'/>
                        </Tooltip>
                      }
                    />

                    {values.issuanceMode === 'mid_live' && (
                      <Input
                        type="password"
                        name="apikey"
                        id='apikey'
                        value={values.apiKey}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        label="API Key"
                        showPasswordIcon
                        appendLabelIcon={
                          <Tooltip
                            placement="bottom"
                            bgColor="bg-black"
                            textColor="text-white"
                            containerClassName="rounded-full"
                            tooltipClassName="w-[350px] text-xs"
                            message={"A confidential authentication token that authorizes your system to create and submit policies to the MID. This key is issued by the National Insurance Commission (NIC) and must be securely stored and never exposed publicly."}>
                            <HelpCircle className='h-3 w-3 ml-1 dark:text-white cursor-pointer'/>
                          </Tooltip>
                        }
                      />
                    )}

                    <Input
                      type={'text'}
                      name="branchCode"
                      value={values.branchCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Branch Code"
                      placeholder="Enter Branch Code provided by this insurance company"
                      appendLabelIcon={
                        <Tooltip
                          placement="bottom"
                          bgColor="bg-black"
                          textColor="text-white"
                          containerClassName="rounded-full"
                          tooltipClassName="w-[350px] text-xs"
                          message={"A unique identifier representing the specific branch of the insurance company under which the policy is being issued. This code is provided internally by the insurance company."}>
                          <HelpCircle className='h-3 w-3 ml-1 dark:text-white cursor-pointer'/>
                        </Tooltip>
                      }
                      className="rounded"
                    />

                    <Input
                      type={'text'}
                      name="intermediaryCode"
                      value={values.intermediaryCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      label="Intermediary Code"
                      placeholder="Enter the intermediary code provided  by this insurance company"
                      appendLabelIcon={
                        <Tooltip
                          placement="bottom"
                          bgColor="bg-black"
                          textColor="text-white"
                          containerClassName="rounded-full"
                          tooltipClassName="w-[350px] text-xs"
                          message={"A unique identifier assigned to you (the broker/agent) by your insurance company. It is used to track and attribute all transactions and policy activities initiated through your account. You can obtain this directly from your insurance provider."}>
                          <HelpCircle className='h-3 w-3 ml-1 dark:text-white cursor-pointer'/>
                        </Tooltip>
                      }
                      className="rounded"
                    />

                   {!user.isBroker() && <SelectDropdown
                      name="type"
                      listSelectedValue={values.type}
                      onChange={(value: number) => {
                        setFieldValue('type', value);
                      }}
                      onBlur={handleBlur}
                      label="Company Type"
                      placeholder="Select a company type"
                      list={Object.keys(INTERMEDIARY_TYPES).map((type) => ({
                        name: type,
                        value: type
                      }))}
                      required={!user.isBroker() ? true : false}
                      error={touched.type ? errors.type : undefined}
                      helperText={touched?.type ? errors.type : undefined}
                    />}

                      <FieldArray name="contacts">
                        {({ push, remove }) => (
                          <div className="flex flex-col gap-3">
                            <div className="flex items-center justify-between">
                              <label className="text-sm font-medium dark:text-darkBg-mainText">
                                Contact Persons<span className="text-red-500 ml-1">*</span>
                              </label>
                              <Tooltip
                                placement="left"
                                bgColor="bg-black"
                                textColor="text-white"
                                containerClassName="rounded-full"
                                tooltipClassName="w-[260px] text-xs"
                                message={"List every contact person at this insurance company. Name and email are required; phone and role are optional."}>
                                <HelpCircle className='h-3 w-3 cursor-pointer' />
                              </Tooltip>
                            </div>
                            {values.contacts?.map((contact: IInsurerContactFormValue, index: number) => {
                              const contactErrors = (errors.contacts as any)?.[index] ?? {};
                              const contactTouched = (touched.contacts as any)?.[index] ?? {};
                              return (
                                <div key={index} className="border border-gray-200 dark:border-gray-700 rounded p-3 flex flex-col gap-3">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      {index === 0 ? 'Primary Contact' : `Contact ${index + 1}`}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (contact.id) {
                                          setDeletedContactIds((prev) => [...prev, contact.id as number]);
                                        }
                                        remove(index);
                                      }}
                                      disabled={values.contacts.length <= 1}
                                      className="p-1 text-gray-500 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                      aria-label="Remove contact">
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <Input
                                      type="text"
                                      name={`contacts.${index}.name`}
                                      value={contact.name}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      label="Name"
                                      placeholder="Contact person name"
                                      required
                                      error={contactTouched.name ? contactErrors.name : undefined}
                                      helperText={contactTouched.name ? contactErrors.name : undefined}
                                      className="rounded"
                                    />
                                    <Input
                                      type="email"
                                      name={`contacts.${index}.email`}
                                      value={contact.email}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      label="Email"
                                      placeholder="name@company.com"
                                      required
                                      error={contactTouched.email ? contactErrors.email : undefined}
                                      helperText={contactTouched.email ? contactErrors.email : undefined}
                                      className="rounded"
                                    />
                                    <Input
                                      type="text"
                                      name={`contacts.${index}.phone`}
                                      value={contact.phone}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      label="Phone"
                                      placeholder="Phone number"
                                      error={contactTouched.phone ? contactErrors.phone : undefined}
                                      helperText={contactTouched.phone ? contactErrors.phone : undefined}
                                      className="rounded"
                                    />
                                    <Input
                                      type="text"
                                      name={`contacts.${index}.role`}
                                      value={contact.role}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      label="Role"
                                      placeholder="e.g. Claims Manager"
                                      error={contactTouched.role ? contactErrors.role : undefined}
                                      helperText={contactTouched.role ? contactErrors.role : undefined}
                                      className="rounded"
                                    />
                                  </div>
                                </div>
                              );
                            })}
                            {!readOnly && (
                              <button
                                type="button"
                                onClick={() => push({ ...EMPTY_CONTACT })}
                                className="flex items-center gap-2 text-sm text-primary-main hover:underline w-fit mt-1">
                                <Plus className="h-4 w-4" />
                                Add Another Contact
                              </button>
                            )}
                          </div>
                        )}
                      </FieldArray>

                    <FieldArray name="motorPolicies">
                      {({ push, remove }) => (
                        <div className="flex flex-col gap-3">
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium dark:text-darkBg-mainText">
                              Motor Policies<span className="text-red-500 ml-1">*</span>
                            </label>
                            <Tooltip
                              placement="left"
                              bgColor="bg-black"
                              textColor="text-white"
                              containerClassName="rounded-full"
                              tooltipClassName="w-[260px] text-xs"
                              message={"List every motor policy you offer with its commission rate. Comprehensive and Third Party are included by default."}>
                              <HelpCircle className='h-3 w-3 cursor-pointer' />
                            </Tooltip>
                          </div>
                          {values.motorPolicies?.map((policy: IMotorPolicyFormValue, index: number) => {
                            const nameError = (errors.motorPolicies as any)?.[index]?.name;
                            const rateError = (errors.motorPolicies as any)?.[index]?.rate;
                            const nameTouched = (touched.motorPolicies as any)?.[index]?.name;
                            const rateTouched = (touched.motorPolicies as any)?.[index]?.rate;
                            return (
                              <div key={index} className="flex items-start gap-3">
                                <div className="flex-1">
                                  <Input
                                    type="text"
                                    name={`motorPolicies.${index}.name`}
                                    value={policy.name}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="Policy name"
                                    error={nameTouched ? nameError : undefined}
                                    helperText={nameTouched ? nameError : undefined}
                                    className="rounded"
                                  />
                                </div>
                                <div className="w-40">
                                  <Input
                                    type="number"
                                    name={`motorPolicies.${index}.rate`}
                                    value={policy.rate}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="Rate %"
                                    error={rateTouched ? rateError : undefined}
                                    helperText={rateTouched ? rateError : undefined}
                                    className="rounded"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (policy.id) {
                                      setDeletedPolicyIds((prev) => [...prev, policy.id as number]);
                                    }
                                    remove(index);
                                  }}
                                  disabled={values.motorPolicies.length <= 1}
                                  className="mt-2 p-2 text-gray-500 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                  aria-label="Remove policy">
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            );
                          })}
                          {!readOnly && (
                            <button
                              type="button"
                              onClick={() => push({ name: '', rate: '' })}
                              className="flex items-center gap-2 text-sm text-primary-main hover:underline w-fit mt-1">
                              <Plus className="h-4 w-4" />
                              Add Another Motor Policy
                            </button>
                          )}
                        </div>
                      )}
                    </FieldArray>
                  </div>
                  </fieldset>
                  <div className="w-full flex gap-4 mt-5">
                    {readOnly ? (
                      <Button
                        label="Close"
                        bgColor='primary'
                        fullWidth
                        onClick={onClose}
                      />
                    ) : (
                      <>
                        <Button
                          label="Cancel"
                          bgColor='secondary'
                          fullWidth
                          onClick={onClose}
                        />
                        <Button
                          label={isCreateRequest ? 'Create Insurer' : 'Update'}
                          type="submit"
                          bgColor='primary'
                          fullWidth
                          loading={submitting}
                          disabled={submitting}
                        />
                      </>
                    )}
                  </div>
                </form>
              );
            }}
          </Formik>
        </div>
      )}
    </>
  );
};

export default CreateBrokerIntermediary;
